import ReactGA from 'react-ga4';

// GA4 Measurement ID from environment variables
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Debug mode for development
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Initialize Google Analytics 4
 * Only initializes if:
 * - GA_MEASUREMENT_ID is configured
 * - User has accepted cookies (GDPR compliance)
 */
export const initGA = (): boolean => {
  // Check if GA ID is configured
  if (!GA_MEASUREMENT_ID) {
    if (isDevelopment) {
      console.warn('Google Analytics not initialized: REACT_APP_GA_MEASUREMENT_ID not set');
    }
    return false;
  }

  // Check cookie consent (GDPR compliance)
  const cookieConsent = localStorage.getItem('cookieConsent');
  if (cookieConsent !== 'accepted') {
    if (isDevelopment) {
      console.info('Google Analytics not initialized: Cookie consent required');
    }
    return false;
  }

  try {
    // Initialize GA4
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      gaOptions: {
        debug_mode: isDevelopment,
        anonymize_ip: true, // GDPR compliance
      },
      // Az oldalmegtekintést a usePageTracking küldi (redaktált útvonallal); az automatikus
      // első megtekintés a teljes URL-t — akár egy jelszó-visszaállító tokent — küldené.
      gtagOptions: { send_page_view: false },
    });

    if (isDevelopment) {
      console.log('Google Analytics initialized:', GA_MEASUREMENT_ID);
    }

    return true;
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
    return false;
  }
};

/** Titkot (tokent) hordozó útvonalak: az analitikába csak az útvonal eleje kerül. */
const REDACTED_PATH_PREFIXES = ['/jelszo-visszaallitas', '/hirlevel/megerosites', '/leiratkozas', '/latvanyterv'];

/** Zárt vásárlói csoportok: a meghívó kód és a csoport webcíme helyett helyőrző. */
const PLACEHOLDER_SEGMENTS: Array<[string, string]> = [['/csatlakozas', '[kód]'], ['/csoport', '[csoport]']];

/** A `?meghivo=` (meghívó kód) értéke a query-ben helyőrzőre cserélve. */
const redactInviteQuery = (path: string): string =>
  path.replace(/([?&]meghivo=)[^&#]*/g, '$1[kód]').replace(/([?&]next=)[^&#]*csatlakozas[^&#]*/g, '$1[kód]');

/** A tokent tartalmazó útvonalat (és a query-t) levágja: `/jelszo-visszaallitas/x/y` → `/jelszo-visszaallitas`;
 *  a meghívó kódot és a csoport webcímét helyőrzőre cseréli (`/csatlakozas/[kód]`, `/csoport/[csoport]`). */
export const redactPath = (path: string): string => {
  const prefix = REDACTED_PATH_PREFIXES.find((p) => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`));
  if (prefix) return prefix;
  const placeholder = PLACEHOLDER_SEGMENTS.find(([p]) => path.startsWith(`${p}/`));
  if (placeholder) return `${placeholder[0]}/${placeholder[1]}`;
  return redactInviteQuery(path);
};

/**
 * Track page view
 */
export const trackPageView = (path: string, title?: string) => {
  if (!GA_MEASUREMENT_ID) return;

  try {
    const page = redactPath(path);
    // A redaktált címet a gtag-konfigurációra is beállítjuk, így a későbbi események
    // (trackEvent) sem viszik a tokenes URL-t.
    ReactGA.set({ page_location: `${window.location.origin}${page}`, page_path: page });
    ReactGA.send({
      hitType: 'pageview',
      page,
      // A teljes cím is redaktálva (a gtag alapból a document.location-t küldené).
      page_location: `${window.location.origin}${page}`,
      title: title || document.title,
    });

    if (isDevelopment) {
      console.log('GA Pageview:', path, title);
    }
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
};

/**
 * Track custom event
 */
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number
) => {
  if (!GA_MEASUREMENT_ID) return;

  try {
    ReactGA.event({
      category,
      action,
      label,
      value,
    });

    if (isDevelopment) {
      console.log('GA Event:', { category, action, label, value });
    }
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

/**
 * E-commerce: Add to cart
 */
export const trackAddToCart = (
  productId: number,
  productName: string,
  price: number,
  quantity: number = 1
) => {
  if (!GA_MEASUREMENT_ID) return;

  try {
    ReactGA.event('add_to_cart', {
      currency: 'HUF',
      value: price * quantity,
      items: [
        {
          item_id: productId.toString(),
          item_name: productName,
          price: price,
          quantity: quantity,
        },
      ],
    });

    if (isDevelopment) {
      console.log('GA Add to Cart:', { productId, productName, price, quantity });
    }
  } catch (error) {
    console.error('Failed to track add to cart:', error);
  }
};

/**
 * E-commerce: Remove from cart
 */
export const trackRemoveFromCart = (
  productId: number,
  productName: string,
  price: number,
  quantity: number = 1
) => {
  if (!GA_MEASUREMENT_ID) return;

  try {
    ReactGA.event('remove_from_cart', {
      currency: 'HUF',
      value: price * quantity,
      items: [
        {
          item_id: productId.toString(),
          item_name: productName,
          price: price,
          quantity: quantity,
        },
      ],
    });

    if (isDevelopment) {
      console.log('GA Remove from Cart:', { productId, productName, price, quantity });
    }
  } catch (error) {
    console.error('Failed to track remove from cart:', error);
  }
};

/**
 * E-commerce: View item
 */
export const trackViewItem = (
  productId: number,
  productName: string,
  price: number,
  category?: string
) => {
  if (!GA_MEASUREMENT_ID) return;

  try {
    ReactGA.event('view_item', {
      currency: 'HUF',
      value: price,
      items: [
        {
          item_id: productId.toString(),
          item_name: productName,
          item_category: category,
          price: price,
        },
      ],
    });

    if (isDevelopment) {
      console.log('GA View Item:', { productId, productName, price, category });
    }
  } catch (error) {
    console.error('Failed to track view item:', error);
  }
};

/**
 * E-commerce: Begin checkout
 */
export const trackBeginCheckout = (
  totalValue: number,
  items: Array<{
    id: number;
    name: string;
    price: number;
    quantity: number;
    category?: string;
  }>
) => {
  if (!GA_MEASUREMENT_ID) return;

  try {
    ReactGA.event('begin_checkout', {
      currency: 'HUF',
      value: totalValue,
      items: items.map((item) => ({
        item_id: item.id.toString(),
        item_name: item.name,
        item_category: item.category,
        price: item.price,
        quantity: item.quantity,
      })),
    });

    if (isDevelopment) {
      console.log('GA Begin Checkout:', { totalValue, items });
    }
  } catch (error) {
    console.error('Failed to track begin checkout:', error);
  }
};

/**
 * E-commerce: Purchase
 */
export const trackPurchase = (
  transactionId: string,
  totalValue: number,
  tax: number,
  shipping: number,
  items: Array<{
    id: number;
    name: string;
    price: number;
    quantity: number;
    category?: string;
  }>
) => {
  if (!GA_MEASUREMENT_ID) return;

  try {
    ReactGA.event('purchase', {
      transaction_id: transactionId,
      currency: 'HUF',
      value: totalValue,
      tax: tax,
      shipping: shipping,
      items: items.map((item) => ({
        item_id: item.id.toString(),
        item_name: item.name,
        item_category: item.category,
        price: item.price,
        quantity: item.quantity,
      })),
    });

    if (isDevelopment) {
      console.log('GA Purchase:', { transactionId, totalValue, tax, shipping, items });
    }
  } catch (error) {
    console.error('Failed to track purchase:', error);
  }
};

/**
 * Track search
 */
export const trackSearch = (searchTerm: string) => {
  if (!GA_MEASUREMENT_ID) return;

  try {
    ReactGA.event('search', {
      search_term: searchTerm,
    });

    if (isDevelopment) {
      console.log('GA Search:', searchTerm);
    }
  } catch (error) {
    console.error('Failed to track search:', error);
  }
};

/**
 * Track user signup
 */
export const trackSignUp = (method: string = 'email') => {
  if (!GA_MEASUREMENT_ID) return;

  try {
    ReactGA.event('sign_up', {
      method: method,
    });

    if (isDevelopment) {
      console.log('GA Sign Up:', method);
    }
  } catch (error) {
    console.error('Failed to track sign up:', error);
  }
};

/**
 * Track user login
 */
export const trackLogin = (method: string = 'email') => {
  if (!GA_MEASUREMENT_ID) return;

  try {
    ReactGA.event('login', {
      method: method,
    });

    if (isDevelopment) {
      console.log('GA Login:', method);
    }
  } catch (error) {
    console.error('Failed to track login:', error);
  }
};

/**
 * Track wishlist add
 */
export const trackAddToWishlist = (
  productId: number,
  productName: string,
  price: number
) => {
  if (!GA_MEASUREMENT_ID) return;

  try {
    ReactGA.event('add_to_wishlist', {
      currency: 'HUF',
      value: price,
      items: [
        {
          item_id: productId.toString(),
          item_name: productName,
          price: price,
        },
      ],
    });

    if (isDevelopment) {
      console.log('GA Add to Wishlist:', { productId, productName, price });
    }
  } catch (error) {
    console.error('Failed to track add to wishlist:', error);
  }
};
